import React, { useState, useEffect, useRef } from 'react';
import logoSymbol from '../images/ThiranX Logo Symbol.png';

/* ── Brand palette ──────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

/* ── Particle factory ───────────────────────────────────────── */
const makeParticles = (n) =>
  Array.from({ length: n }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2.5 + 1,
    duration: Math.random() * 18 + 14,
    delay: Math.random() * 4,
    opacity: Math.random() * 0.45 + 0.08,
    color: i % 3 === 0 ? V1 : i % 3 === 1 ? V2 : '#d4b8ff',
  }));

/*
  Phase schedule (ms from mount):
  ─────────────────────────────────────────────
  0 → 300   : dark canvas
  300        : phase 1 — BG orbs + rings fade in
  900        : phase 2 — logo drops in
  1600       : phase 3 — brand name sweeps in
  2100       : phase 4 — tagline + progress bar
  2100-3900  : progress bar fills (1800 ms)
  4200       : exit begins
  4900       : onComplete fires
*/

const SplashScreen = ({ onComplete }) => {
  const [phase, setPhase]   = useState(0);
  const [done, setDone]     = useState(false);   // bar finished
  const [exiting, setExiting] = useState(false);

  const particles = useRef(makeParticles(28)).current;

  /* ── Phase timeline ─────────────────────────────────────── */
  useEffect(() => {
    const BAR_DURATION = 2000; // must match txBarFill animation-duration
    const t = [
      setTimeout(() => setPhase(1), 300),
      setTimeout(() => setPhase(2), 900),
      setTimeout(() => setPhase(3), 1600),
      setTimeout(() => setPhase(4), 2100),
      // bar starts at phase 4 (2100ms) + BAR_DURATION + 300ms pause
      setTimeout(() => setDone(true),                   2100 + BAR_DURATION),
      setTimeout(() => setExiting(true),                2100 + BAR_DURATION + 350),
      setTimeout(() => onComplete?.(),                  2100 + BAR_DURATION + 350 + 750),
    ];
    return () => t.forEach(clearTimeout);
  }, [onComplete]);

  /* ── Transition helpers ─────────────────────────────────── */
  const tr  = (dur, del = 0, ease = 'cubic-bezier(0.4,0,0.2,1)') =>
    `all ${dur}ms ${ease} ${del}ms`;
  const vis = (show, dur = 600, del = 0) => ({
    opacity:    show ? 1 : 0,
    transition: tr(dur, del),
  });

  return (
    <>
      {/* ── Google Fonts inline import ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&display=swap');

        /* Floating particles */
        @keyframes txFloat {
          0%,100% { transform: translateY(0) translateX(0) scale(1); }
          33%      { transform: translateY(-22px) translateX(9px) scale(1.05); }
          66%      { transform: translateY(-10px) translateX(-8px) scale(0.96); }
        }

        /* Ring spins */
        @keyframes txSpin  { to { transform: rotate(360deg);  } }
        @keyframes txSpinR { to { transform: rotate(-360deg); } }

        /* Logo continuous gentle float after reveal */
        @keyframes txLogoFloat {
          0%,100% { transform: translateY(0px);   }
          50%      { transform: translateY(-8px);  }
        }

        /* Logo glow pulse */
        @keyframes txLogoPulse {
          0%,100% { filter: drop-shadow(0 0 18px ${V1}99) drop-shadow(0 0 36px ${V1}44); }
          50%      { filter: drop-shadow(0 0 32px ${V1}dd) drop-shadow(0 0 60px ${V1}88) drop-shadow(0 0 90px ${V2}55); }
        }

        /* Letter drop-in */
        @keyframes txLetterDrop {
          0%   { opacity:0; transform: translateY(-36px) rotateX(80deg); filter: blur(6px); }
          100% { opacity:1; transform: translateY(0)     rotateX(0deg);  filter: blur(0);   }
        }

        /* Text glow bloom — breathes behind brand name */
        @keyframes txGlowBloom {
          0%,100% { opacity: 0.18; transform: scale(1);    }
          50%      { opacity: 0.38; transform: scale(1.12); }
        }

        /* Underline draw — expands from center */
        @keyframes txUnderlineDraw {
          0%   { transform: scaleX(0); opacity: 0; }
          40%  { opacity: 1; }
          100% { transform: scaleX(1); opacity: 1; }
        }

        /* Tagline word slide */
        @keyframes txTagSlide {
          0%   { opacity:0; transform: translateY(14px); }
          100% { opacity:1; transform: translateY(0);    }
        }

        /* Progress bar entrance */
        @keyframes txBarIn {
          0%   { opacity:0; transform: scaleY(0) translateY(4px); }
          100% { opacity:1; transform: scaleY(1) translateY(0);   }
        }

        /* Bar fill — pure CSS, buttery smooth */
        @keyframes txBarFill {
          0%   { width: 0%; }
          15%  { width: 12%; }
          40%  { width: 45%; }
          70%  { width: 78%; }
          88%  { width: 92%; }
          100% { width: 100%; }
        }

        /* Shimmer that rides inside the filled bar */
        @keyframes txBarShimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(400%);  }
        }

        /* Exit */
        @keyframes txExit {
          0%   { opacity:1; transform: scale(1);    }
          100% { opacity:0; transform: scale(1.06); }
        }

        /* Dot blink for loading text */
        @keyframes txBlink {
          0%,100% { opacity:0.25; } 50% { opacity:1; }
        }

        .tx-splash-exit {
          animation: txExit 750ms cubic-bezier(0.4,0,0.2,1) forwards !important;
        }
      `}</style>

      <div
        className={exiting ? 'tx-splash-exit' : ''}
        style={{
          position:       'fixed',
          inset:          0,
          zIndex:         99999,
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          background:     '#08000f',
          overflow:       'hidden',
          fontFamily:     "'Space Grotesk', 'Inter', sans-serif",
        }}
      >

        {/* ── BG gradient orbs ── */}
        <div style={{
          position:   'absolute', inset: 0, pointerEvents: 'none',
          background: `
            radial-gradient(ellipse 65% 55% at 50% 50%, ${V4}77 0%, transparent 68%),
            radial-gradient(ellipse 45% 45% at 18% 82%, ${V3}44 0%, transparent 65%),
            radial-gradient(ellipse 40% 38% at 82% 18%, ${V1}33 0%, transparent 65%)
          `,
          ...vis(phase >= 1, 1400),
        }} />

        {/* ── Floating particles ── */}
        {particles.map(p => (
          <div key={p.id} style={{
            position:     'absolute',
            left:         `${p.x}%`,
            top:          `${p.y}%`,
            width:        p.size,
            height:       p.size,
            borderRadius: '50%',
            background:   p.color,
            ...vis(phase >= 1, 1200, p.delay * 100),
            animation:    phase >= 1
              ? `txFloat ${p.duration}s ease-in-out infinite ${p.delay}s`
              : 'none',
            pointerEvents: 'none',
          }} />
        ))}

        {/* ── Circuit SVG grid ── */}
        <svg
          viewBox="0 0 800 600"
          preserveAspectRatio="xMidYMid slice"
          style={{
            position:      'absolute',
            width:         '100%',
            height:        '100%',
            pointerEvents: 'none',
            ...vis(phase >= 1, 1200),
            opacity: phase >= 1 ? 0.07 : 0,
          }}
        >
          <g stroke={V2} strokeWidth="0.7" fill="none">
            <line x1="0" y1="150" x2="800" y2="150"
              strokeDasharray="800"
              strokeDashoffset={phase >= 1 ? 0 : 800}
              style={{ transition: 'stroke-dashoffset 2.4s ease 0.2s' }} />
            <line x1="0" y1="450" x2="800" y2="450"
              strokeDasharray="800"
              strokeDashoffset={phase >= 1 ? 0 : 800}
              style={{ transition: 'stroke-dashoffset 2.4s ease 0.5s' }} />
            <line x1="200" y1="0" x2="200" y2="600"
              strokeDasharray="600"
              strokeDashoffset={phase >= 1 ? 0 : 600}
              style={{ transition: 'stroke-dashoffset 2.4s ease 0.7s' }} />
            <line x1="600" y1="0" x2="600" y2="600"
              strokeDasharray="600"
              strokeDashoffset={phase >= 1 ? 0 : 600}
              style={{ transition: 'stroke-dashoffset 2.4s ease 0.9s' }} />
            {[
              [200, 150], [600, 150], [200, 450], [600, 450]
            ].map(([cx, cy], i) => (
              <circle key={i} cx={cx} cy={cy} r="3.5"
                fill={i % 2 === 0 ? V1 : V2}
                opacity={phase >= 1 ? 0.8 : 0}
                style={{ transition: `opacity 0.5s ease ${1 + i * 0.2}s` }} />
            ))}
          </g>
        </svg>

        {/* ── Outer ring (slow spin) ── */}
        <div style={{
          position:     'absolute',
          width:        'min(310px, 65vw)',
          height:       'min(310px, 65vw)',
          borderRadius: '50%',
          border:       `1.5px solid ${V1}1e`,
          animation:    phase >= 1 ? 'txSpin 10s linear infinite' : 'none',
          ...vis(phase >= 1, 1000),
          pointerEvents:'none',
        }}>
          <div style={{
            position:     'absolute', top: -4, left: '50%',
            transform:    'translateX(-50%)',
            width: 7, height: 7, borderRadius: '50%', background: V1,
            boxShadow:    `0 0 14px ${V1}, 0 0 28px ${V1}88`,
          }} />
        </div>

        {/* ── Inner ring (counter-spin) ── */}
        <div style={{
          position:     'absolute',
          width:        'min(380px, 78vw)',
          height:       'min(380px, 78vw)',
          borderRadius: '50%',
          border:       `1px solid ${V2}11`,
          animation:    phase >= 2 ? 'txSpinR 14s linear infinite' : 'none',
          ...vis(phase >= 2, 1200),
          opacity:      phase >= 2 ? 0.55 : 0,
          pointerEvents:'none',
        }}>
          <div style={{
            position:  'absolute', top: '50%', right: -4,
            transform: 'translateY(-50%)',
            width: 5, height: 5, borderRadius: '50%', background: V2,
            boxShadow: `0 0 10px ${V2}`,
          }} />
        </div>

        {/* ══ MAIN CONTENT ══ */}
        <div style={{
          position:      'relative', zIndex: 10,
          display:       'flex', flexDirection: 'column', alignItems: 'center',
          gap:           0,
          perspective:   '800px',
        }}>

          {/* Logo */}
          <div style={{
            width:        'min(96px, 21vw)',
            height:       'min(96px, 21vw)',
            marginBottom: 28,
            opacity:      phase >= 2 ? 1 : 0,
            transform:    phase >= 2
              ? 'translateY(0) scale(1)'
              : 'translateY(30px) scale(0.55)',
            transition:   'opacity 0.7s cubic-bezier(0.34,1.56,0.64,1) 0s, transform 0.7s cubic-bezier(0.34,1.56,0.64,1) 0s',
            animation:    phase >= 2
              ? 'txLogoFloat 4s ease-in-out infinite 0.8s, txLogoPulse 3s ease-in-out infinite 0.8s'
              : 'none',
          }}>
            <img
              src={logoSymbol}
              alt="ThiranX"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>

          {/* Brand name — letter-by-letter */}
          <div style={{
            fontFamily:   "'Space Grotesk', 'Inter', sans-serif",
            fontWeight:   800,
            fontSize:     'clamp(40px, 10.5vw, 76px)',
            letterSpacing:'-0.03em',
            lineHeight:   1,
            marginBottom: 10,
            position:     'relative',
            perspective:  '600px',
          }}>
            {'ThiranX'.split('').map((char, i) => {
              const isX = i === 6;
              return (
                <span
                  key={i}
                  style={{
                    display:   'inline-block',
                    animation: phase >= 3
                      ? `txLetterDrop 0.55s cubic-bezier(0.34,1.56,0.64,1) ${0.06 * i}s forwards`
                      : 'none',
                    opacity:  phase >= 3 ? undefined : 0,
                    color:    isX ? 'transparent' : '#ede8ff',
                    background: isX
                      ? `linear-gradient(135deg, ${V1} 0%, ${V2} 60%, #d4a0ff 100%)`
                      : 'none',
                    WebkitBackgroundClip: isX ? 'text' : 'unset',
                    backgroundClip:       isX ? 'text' : 'unset',
                  }}
                >
                  {char}
                </span>
              );
            })}

            {/* Glow bloom behind text */}
            <div style={{
              position:      'absolute',
              inset:         '-30px -40px',
              borderRadius:  '50%',
              background:    `radial-gradient(ellipse 80% 60% at 50% 50%, ${V1}33 0%, ${V3}18 45%, transparent 75%)`,
              pointerEvents: 'none',
              animation:     phase >= 3 ? 'txGlowBloom 3.5s ease-in-out infinite 0.4s' : 'none',
              opacity:       phase >= 3 ? 0.28 : 0,
              transition:    'opacity 0.8s ease',
              zIndex:        -1,
            }} />
          </div>

          {/* Animated underline accent under brand name */}
          <div style={{
            width:          'clamp(120px, 28vw, 220px)',
            height:         2,
            borderRadius:   99,
            background:     `linear-gradient(90deg, transparent, ${V1}, ${V2}, ${V1}, transparent)`,
            marginBottom:   12,
            transformOrigin:'center',
            animation:      phase >= 3 ? 'txUnderlineDraw 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.45s both' : 'none',
            opacity:        phase >= 3 ? 1 : 0,
            boxShadow:      `0 0 12px ${V1}66, 0 0 24px ${V1}33`,
          }} />

          {/* Tagline */}
          <p style={{
            fontFamily:    "'Space Grotesk', 'Inter', sans-serif",
            fontSize:      'clamp(10px, 2.8vw, 13px)',
            fontWeight:    600,
            letterSpacing: '0.26em',
            textTransform: 'uppercase',
            color:         V2,
            marginBottom:  44,
            animation:     phase >= 4 ? 'txTagSlide 0.55s ease forwards' : 'none',
            opacity:       phase >= 4 ? undefined : 0,
          }}>
            Skills That Get You Hired
          </p>

          {/* Progress bar */}
          <div style={{
            width:          'min(260px, 56vw)',
            height:         4,
            borderRadius:   99,
            background:     `rgba(127,0,255,0.10)`,
            border:         `1px solid rgba(127,0,255,0.18)`,
            overflow:       'hidden',
            position:       'relative',
            opacity:        phase >= 4 ? 1 : 0,
            transform:      phase >= 4 ? 'scaleY(1) translateY(0)' : 'scaleY(0) translateY(4px)',
            transition:     'opacity 0.45s ease, transform 0.45s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            {/* Filled bar — pure CSS animation */}
            {phase >= 4 && (
              <div style={{
                position:           'absolute',
                top:                0, left: 0,
                height:             '100%',
                borderRadius:       99,
                background:         `linear-gradient(90deg, ${V1} 0%, ${V2} 55%, #d4a0ff 100%)`,
                boxShadow:          `0 0 8px ${V1}aa, 0 0 20px ${V1}55`,
                animation:          `txBarFill 2000ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
                overflow:           'hidden',
              }}>
                {/* Moving shimmer inside bar */}
                <div style={{
                  position:   'absolute',
                  top:        0, bottom: 0,
                  width:      '35%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                  animation:  'txBarShimmer 1.6s ease-in-out infinite',
                  borderRadius: 99,
                }} />
              </div>
            )}
          </div>

          {/* Status text */}
          <div style={{
            fontFamily:    "'Inter', sans-serif",
            fontSize:      'clamp(10px, 2.4vw, 11.5px)',
            fontWeight:    500,
            color:         '#6b5090',
            marginTop:     14,
            letterSpacing: '0.06em',
            opacity:       phase >= 4 ? 1 : 0,
            transition:    'opacity 0.5s ease 0.3s',
            display:       'flex', alignItems: 'center', gap: 5,
            minHeight:     18,
          }}>
            {done ? (
              <span style={{
                color:      V2,
                fontWeight: 600,
                animation:  'txTagSlide 0.4s ease forwards',
              }}>Welcome ✦</span>
            ) : (
              <>
                Initializing experience
                <span style={{ animation: 'txBlink 1.2s ease-in-out infinite 0.0s' }}>.</span>
                <span style={{ animation: 'txBlink 1.2s ease-in-out infinite 0.2s' }}>.</span>
                <span style={{ animation: 'txBlink 1.2s ease-in-out infinite 0.4s' }}>.</span>
              </>
            )}
          </div>
        </div>

        {/* ── Bottom gradient line ── */}
        <div style={{
          position:   'absolute', bottom: 0, left: 0, right: 0,
          height:     1,
          background: `linear-gradient(90deg, transparent, ${V1}55, ${V2}55, ${V1}55, transparent)`,
          ...vis(phase >= 1, 1200),
        }} />

        {/* ── Corner brackets ── */}
        {[
          { top: 'clamp(18px,4vw,36px)',    left:  'clamp(18px,4vw,36px)'   },
          { bottom: 'clamp(18px,4vw,36px)', right: 'clamp(18px,4vw,36px)'  },
        ].map((pos, idx) => (
          <div key={idx} style={{
            position: 'absolute', ...pos,
            ...vis(phase >= 2, 800),
            opacity:  phase >= 2 ? 0.22 : 0,
          }}>
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
              {idx === 0
                ? <path d="M0 15 L0 0 L15 0" stroke={V1} strokeWidth="1.5" />
                : <path d="M30 15 L30 30 L15 30" stroke={V1} strokeWidth="1.5" />}
              <circle
                cx={idx === 0 ? 0 : 30}
                cy={idx === 0 ? 0 : 30}
                r="2" fill={V2}
              />
            </svg>
          </div>
        ))}

        {/* ── Version badge ── */}
        <div style={{
          position:      'absolute',
          bottom:        'clamp(18px,4vw,32px)',
          left:          '50%',
          transform:     'translateX(-50%)',
          fontFamily:    "'Inter', sans-serif",
          fontSize:      10,
          fontWeight:    600,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color:         '#3d2860',
          ...vis(phase >= 4, 600),
        }}>
          ThiranX Platform
        </div>

      </div>
    </>
  );
};

export default SplashScreen;
